import { useEffect, useMemo, useState } from 'react'
import to from 'await-catch'
import moment from 'moment'
import { getPastPatientAppointments, getAttendanceRate } from '../api'
import { useClinicsOptions } from './useClinicsOptions'

const formatApiDate = value => (value ? moment(value).format('YYYY-MM-DD') : undefined)

export const usePastAppointments = ({ nroDocumento, addFlashMessage, filters = {} }) => {
	const [pastAppointments, setPastAppointments] = useState([])
	const [attendance, setAttendance] = useState(null)
	const [isLoading, setIsLoading] = useState(true)
	const { clinicsOptions, clinics } = useClinicsOptions({ addFlashMessage })

	const { fechaDesde, fechaHasta, nombreProfesional, idClinica } = filters

	useEffect(() => {
		if (!nroDocumento) {
			setPastAppointments([])
			setIsLoading(false)
			return
		}

		let isMounted = true
		setIsLoading(true)

		const fetchPastAppointments = async () => {
			const [err, { data } = {}] = await to(
				getPastPatientAppointments({
					nroDocumento,
					fechaDesde: formatApiDate(fechaDesde),
					fechaHasta: formatApiDate(fechaHasta),
					nombreProfesional: nombreProfesional || undefined,
					idClinica: idClinica || undefined
				})
			)
			if (!isMounted) return

			if (err) {
				addFlashMessage('Ocurrió un error cargando el historial de turnos')
				setIsLoading(false)
				return
			}

			setPastAppointments(Array.isArray(data) ? data : [])
			setIsLoading(false)
		}

		fetchPastAppointments()

		return () => {
			isMounted = false
		}
	}, [addFlashMessage, nroDocumento, fechaDesde, fechaHasta, nombreProfesional, idClinica])

	useEffect(() => {
		if (!nroDocumento) {
			setAttendance(null)
			return
		}

		let isMounted = true

		const fetchAttendance = async () => {
			const [err, { data } = {}] = await to(getAttendanceRate(nroDocumento))
			if (!isMounted || err) return

			setAttendance(data || null)
		}

		fetchAttendance()

		return () => {
			isMounted = false
		}
	}, [nroDocumento])

	const pastAppointmentsWithAddress = useMemo(
		() =>
			pastAppointments.map(appointment => ({
				...appointment,
				direccion: clinics.find(clinic => clinic.nombre === appointment.establecimiento)?.direccion
			})),
		[pastAppointments, clinics]
	)

	const attendanceRate = useMemo(() => {
		const presente = attendance?.presente || 0
		const cancelado = attendance?.cancelado || 0
		const ausente = attendance?.ausente || 0
		const attended = presente + cancelado
		const total = presente + cancelado + ausente
		const percentage = total > 0 ? Math.round((attended / total) * 100) : 0

		return { total, attended, percentage }
	}, [attendance])

	return { pastAppointments: pastAppointmentsWithAddress, attendanceRate, clinicsOptions, isLoading }
}
